import {
  orderSectionsLikeRunner,
  type EventApiTableSettings,
  type EventConditionalDropdown,
  type EventDefinitionEdit,
  type EventDefinitionInput,
  type EventEditorOptions,
  type EventSectionInput,
} from "common";

/** What the definition editor holds while a writer edits one event form. */
export interface DefinitionFormState {
  name: string;
  sections: EventSectionInput[];
  dropdown: EventConditionalDropdown | null;
  apiTable: EventApiTableSettings | null;
  /** The version the form was opened at; every save is pinned to it. Null for a new form. */
  version: string | null;
  /** Field references (section fields or row layout members) that no longer exist, removed on load. */
  droppedFieldCount: number;
  /** Sections whose collection no longer exists on the sample tracker, removed on load. */
  droppedSectionCount: number;
  /** API table sources (format views, data import configs) that no longer exist, removed on load. */
  droppedApiSourceCount: number;
}

export type DefinitionAction =
  | { type: "setName"; name: string }
  | { type: "addSection"; collectionId: string }
  | { type: "removeSection"; index: number }
  | { type: "moveSection"; index: number; direction: -1 | 1 }
  | {
      type: "setSection";
      index: number;
      patch: Partial<Omit<EventSectionInput, "collectionId">>;
    }
  | { type: "setDropdown"; dropdown: EventConditionalDropdown | null }
  | { type: "setApiTable"; apiTable: EventApiTableSettings | null }
  | { type: "reset"; state: DefinitionFormState };

export const emptyDefinitionState = (): DefinitionFormState => ({
  name: "",
  sections: [],
  dropdown: null,
  apiTable: null,
  version: null,
  droppedFieldCount: 0,
  droppedSectionCount: 0,
  droppedApiSourceCount: 0,
});

const newSection = (collectionId: string): EventSectionInput => ({
  collectionId,
  fieldIds: [],
  insert: true,
  update: false,
  multiple: false,
  collapsible: false,
  collapseOnLoad: false,
  multicolumn: [],
});

/**
 * The form state for a stored definition. Sections of a collection that is gone, fields that are
 * gone, row layout members that match neither a field id nor a field name of the section, and API
 * table sources that are gone are removed and counted, so the next save is one the server accepts.
 * A row layout member written as a field name becomes that field's id, the format legacy writes.
 */
export const definitionStateFrom = (
  def: EventDefinitionEdit,
  options: EventEditorOptions
): DefinitionFormState => {
  const collections = new Map(options.collections.map(c => [c.id, c]));
  let droppedSectionCount = 0;
  let droppedFieldCount = 0;
  const sections: EventSectionInput[] = [];
  for (const stored of def.sections) {
    const collection = collections.get(stored.collectionId);
    if (!collection) {
      droppedSectionCount += 1;
      continue;
    }
    const ids = new Set(collection.fields.map(f => f.id));
    const idByName = new Map(collection.fields.map(f => [f.name, f.id]));
    // One count per distinct reference, so a gone field named in a rule too counts once.
    const gone = new Set<string>();
    const fieldIds = stored.fieldIds.filter(id => {
      if (ids.has(id)) return true;
      gone.add(id);
      return false;
    });
    const shown = new Set(fieldIds);
    const multicolumn = stored.multicolumn
      .map(rule =>
        rule.flatMap(member => {
          const id = ids.has(member) ? member : idByName.get(member);
          if (id !== undefined && shown.has(id)) return [id];
          gone.add(member);
          return [];
        })
      )
      .filter(rule => rule.length > 0);
    droppedFieldCount += gone.size;
    sections.push({ ...stored, fieldIds, multicolumn });
  }
  const ordered = orderSectionsLikeRunner(sections);
  const present = new Set(ordered.map(s => s.collectionId));
  const dropdown = def.dropdown
    ? {
        label: def.dropdown.label,
        collectionIds: def.dropdown.collectionIds.filter(id => present.has(id)),
      }
    : null;

  const sourceIds = new Set(options.apiSources.map(s => s.id));
  let droppedApiSourceCount = 0;
  let apiTable: EventApiTableSettings | null = null;
  if (def.apiTable) {
    const kept = def.apiTable.sourceIds.filter(id => {
      if (sourceIds.has(id)) return true;
      droppedApiSourceCount += 1;
      return false;
    });
    apiTable =
      kept.length > 0
        ? {
            sourceIds: kept,
            merge: def.apiTable.merge,
            download: def.apiTable.download,
          }
        : null;
  }

  return {
    name: def.name,
    sections: ordered,
    dropdown,
    apiTable,
    version: def.version,
    droppedFieldCount,
    droppedSectionCount,
    droppedApiSourceCount,
  };
};

/** The index range [start, end] of the contiguous run of one collection's sections around `index`. */
const groupAround = (
  sections: EventSectionInput[],
  index: number
): [number, number] => {
  const id = sections[index].collectionId;
  let start = index;
  while (start > 0 && sections[start - 1].collectionId === id) start -= 1;
  let end = index;
  while (end < sections.length - 1 && sections[end + 1].collectionId === id)
    end += 1;
  return [start, end];
};

const moveSection = (
  state: DefinitionFormState,
  index: number,
  direction: -1 | 1
): DefinitionFormState => {
  const { sections } = state;
  const neighbour = index + direction;
  if (index < 0 || index >= sections.length) return state;
  if (neighbour < 0 || neighbour >= sections.length) return state;
  const next = [...sections];
  if (sections[neighbour].collectionId === sections[index].collectionId) {
    [next[index], next[neighbour]] = [next[neighbour], next[index]];
    return { ...state, sections: next };
  }
  // Across a collection boundary the whole group moves past the neighbouring whole group, so
  // the sections of one collection stay together, as the runner shows them.
  const [ownStart, ownEnd] = groupAround(sections, index);
  const [otherStart, otherEnd] = groupAround(sections, neighbour);
  const first = Math.min(ownStart, otherStart);
  const last = Math.max(ownEnd, otherEnd);
  const own = sections.slice(ownStart, ownEnd + 1);
  const other = sections.slice(otherStart, otherEnd + 1);
  const swapped = direction === 1 ? [...other, ...own] : [...own, ...other];
  next.splice(first, last - first + 1, ...swapped);
  return { ...state, sections: next };
};

export const definitionReducer = (
  state: DefinitionFormState,
  action: DefinitionAction
): DefinitionFormState => {
  switch (action.type) {
    case "setName":
      return { ...state, name: action.name };
    case "addSection": {
      const section = newSection(action.collectionId);
      let last = -1;
      state.sections.forEach((s, i) => {
        if (s.collectionId === action.collectionId) last = i;
      });
      if (last === -1)
        return { ...state, sections: [...state.sections, section] };
      const sections = [...state.sections];
      sections.splice(last + 1, 0, section);
      return { ...state, sections };
    }
    case "removeSection": {
      const removed = state.sections[action.index];
      if (!removed) return state;
      const sections = state.sections.filter((_, i) => i !== action.index);
      const stillThere = sections.some(
        s => s.collectionId === removed.collectionId
      );
      const dropdown =
        state.dropdown && !stillThere
          ? {
              ...state.dropdown,
              collectionIds: state.dropdown.collectionIds.filter(
                id => id !== removed.collectionId
              ),
            }
          : state.dropdown;
      return { ...state, sections, dropdown };
    }
    case "moveSection":
      return moveSection(state, action.index, action.direction);
    case "setSection": {
      const current = state.sections[action.index];
      if (!current) return state;
      let updated: EventSectionInput = { ...current, ...action.patch };
      if (action.patch.fieldIds) {
        const shown = new Set(action.patch.fieldIds);
        // A rule emptied by the change goes; a rule the writer left empty on purpose stays.
        const multicolumn = updated.multicolumn
          .map(rule => ({
            before: rule.length,
            rule: rule.filter(id => shown.has(id)),
          }))
          .filter(({ before, rule }) => rule.length > 0 || before === 0)
          .map(({ rule }) => rule);
        updated = { ...updated, multicolumn };
      }
      const sections = [...state.sections];
      sections[action.index] = updated;
      return { ...state, sections };
    }
    case "setDropdown":
      return { ...state, dropdown: action.dropdown };
    case "setApiTable":
      return { ...state, apiTable: action.apiTable };
    case "reset":
      return action.state;
    default:
      return state;
  }
};

/** The body a save sends: the name trimmed, empty row layouts and a sourceless API table left out. */
export const inputFromState = (
  state: DefinitionFormState
): EventDefinitionInput => ({
  name: state.name.trim(),
  sections: state.sections.map(s => ({
    ...s,
    fieldIds: [...s.fieldIds],
    multicolumn: s.multicolumn
      .filter(rule => rule.length > 0)
      .map(rule => [...rule]),
  })),
  dropdown: state.dropdown
    ? {
        label: state.dropdown.label,
        collectionIds: [...state.dropdown.collectionIds],
      }
    : null,
  apiTable:
    state.apiTable && state.apiTable.sourceIds.length > 0
      ? {
          sourceIds: [...state.apiTable.sourceIds],
          merge: state.apiTable.merge,
          download: state.apiTable.download,
        }
      : null,
});
